/**
 * RPC message formatting — transforms between backend RPC format and internal
 * component format.
 *
 * The backend sends `send_dynamic_data` RPCs like:
 *   { component_type: "render_component_calendar", component_config: { ... } }
 * The widget needs them in the internal format:
 *   { component_type: "calendar_booking", component_id: "calendar_1234", data: { ... } }
 *
 * This module handles both directions.
 */
// ─── Backend -> widget ───────────────────────────────────────────────────────
/**
 * Transform a backend RPC payload into the internal component format.
 * Returns null if the component type is unknown.
 */
export function transformRpcToComponent(componentName, rpcPayload) {
    const componentId = `${componentName}_${Date.now()}`;
    // Legacy senders put the config under component_data — accept both, like production.
    const config = rpcPayload.component_config ?? rpcPayload.component_data;
    switch (componentName) {
        case "calendar":
            return transformCalendar(componentId, config, rpcPayload);
        case "form":
            return transformForm(componentId, config);
        case "buttons":
            return transformButtons(componentId, config, rpcPayload);
        case "quick_replay":
            return transformQuickReplay(componentId, config);
        case "confirmation":
            return transformConfirmation(componentId, rpcPayload);
        default:
            // Already in the internal format — pass through.
            if (rpcPayload.component_type && rpcPayload.component_id) {
                return rpcPayload;
            }
            return null;
    }
}
function transformCalendar(componentId, config, rpcPayload) {
    const configObj = config;
    const slots = configObj?.slots || [];
    const timezone = configObj?.timezone || "UTC";
    return {
        component_type: "calendar_booking",
        component_id: componentId,
        data: {
            available_slots: slots.map((slot) => ({ id: slot.id, datetime: slot.datetime })),
            timezone,
            title: rpcPayload.title || "Select a time slot",
        },
    };
}
function transformForm(componentId, config) {
    const configObj = config;
    const transformedFields = (configObj?.fields || []).map((field) => ({
        name: field.key || field.name || "",
        type: mapFieldType(field.type),
        label: field.description || field.label || field.key || field.name || "",
        required: field.required,
        placeholder: field.placeholder,
        options: field.options,
    }));
    return {
        component_type: "form",
        component_id: componentId,
        data: {
            title: configObj?.title || "Form",
            description: configObj?.description,
            fields: transformedFields,
            submit_label: configObj?.cta || configObj?.submit_label || "Submit",
        },
    };
}
function transformButtons(componentId, config, rpcPayload) {
    return {
        component_type: "buttons",
        component_id: componentId,
        data: {
            title: rpcPayload.title,
            buttons: Array.isArray(config) ? config : [],
        },
    };
}
/**
 * Quick replies render as free-standing chips (no boxed card) and, once picked,
 * show the selection as a user bubble instead of a confirmation card.
 */
function transformQuickReplay(componentId, config) {
    const configObj = config;
    const options = Array.isArray(configObj?.options) ? configObj.options.filter((option) => typeof option === "string" && option.trim().length > 0) : [];
    return {
        component_type: "buttons",
        component_id: componentId,
        data: {
            presentation: "free",
            completionDisplay: "selected_item",
            buttons: options.map((option) => ({
                id: option,
                label: option,
                action: { type: "trigger_component" },
            })),
        },
    };
}
function transformConfirmation(componentId, rpcPayload) {
    return {
        component_type: "confirmation",
        component_id: componentId,
        data: {
            title: rpcPayload.title || "Confirmed",
            message: rpcPayload.message || "",
            icon: rpcPayload.icon || "success",
            details: rpcPayload.details,
        },
    };
}
/** Map backend field type strings to internal FormFieldType */
function mapFieldType(backendType) {
    const typeMap = {
        text: "text",
        string: "text",
        number: "number",
        email: "email",
        phone_number: "tel",
        single_select: "select",
        multiselect: "multiselect",
        checkbox: "boolean",
        date: "date",
        textarea: "textarea",
    };
    return typeMap[backendType] || "text";
}
const RECEIVE_RPC_METHOD = "receive_dynamic_data";
/**
 * Format a component response payload for sending back to the backend via RPC.
 * Used when the user interacts with a component (selects a slot, submits a
 * form, taps a button).
 */
export function formatComponentResponse(componentName, componentId, responseData, originalConfig) {
    let componentData;
    const stepData = responseData.step_data;
    if (componentName === "calendar") {
        const slotId = stepData?.slot_id;
        const configObj = originalConfig;
        const slots = configObj?.slots || [];
        const timezone = configObj?.timezone || "UTC";
        const selectedSlot = slots.find((slot) => slot.id === slotId);
        componentData = {
            id: selectedSlot?.id || slotId,
            datetime: selectedSlot?.datetime || stepData?.slot_datetime,
            timezone,
            slot_time: stepData?.slot_time,
            slot_date: stepData?.slot_date,
        };
    }
    else if (componentName === "form") {
        componentData = responseData.step_data || responseData.all_data;
    }
    else if (componentName === "buttons") {
        const buttonId = stepData?.selected_button;
        const buttons = Array.isArray(originalConfig) ? originalConfig : [];
        const selectedButton = buttons.find((btn) => btn.id === buttonId);
        componentData = selectedButton || { id: buttonId };
    }
    else if (componentName === "quick_replay") {
        componentData = { text: stepData?.selected_button };
    }
    else {
        componentData = responseData;
    }
    // Echo the render payload's tool_id when present.
    const toolId = originalConfig?.tool_id;
    return {
        method: RECEIVE_RPC_METHOD,
        payload: JSON.stringify({
            component_type: `receive_component_${componentName}`,
            component_id: componentId,
            component_data: componentData,
            ...(toolId && { tool_id: toolId }),
        }),
    };
}
/** Format a slash command for sending via RPC. */
export function formatSlashCommand(command) {
    return {
        method: RECEIVE_RPC_METHOD,
        payload: JSON.stringify({
            component_type: "receive_component_slash_command",
            command: command.replace(/^\//, ""),
        }),
    };
}
/**
 * Format a Block Kit interaction for the agent. Envelope mirrors the dashboard
 * test widget the bot bridge was tested against: `component_id` and `values`
 * are always present; `tool_id` is omitted when falsy and `value` when
 * undefined (never sent as null/empty keys).
 */
export function formatBlocksAction(toolId, componentId, action) {
    return {
        method: RECEIVE_RPC_METHOD,
        payload: JSON.stringify({
            component_type: "receive_component_blocks",
            component_id: componentId,
            ...(toolId && { tool_id: toolId }),
            component_data: {
                action_id: action.action_id,
                ...(action.value !== undefined && { value: action.value }),
                values: action.values,
            },
        }),
    };
}
/**
 * Format a raw dynamic-data payload (client packs: disease search/submit).
 * The payload already carries its own `component_type` envelope.
 */
export function formatDynamicData(payload) {
    return {
        method: RECEIVE_RPC_METHOD,
        payload: JSON.stringify(payload),
    };
}
